import { useEffect, useMemo, useState } from 'react';
import { Gauge, Save } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button, Modal } from '../../ui';
import { QuotaScope, type Quota, type UpsertQuotaInput } from '../../gql/types';
import { TextField } from '../shared/FormField';
import { useQuery } from '../../gql/hooks';
import {
  QUOTA_SCOPE_OPTIONS_QUERY,
  type QuotaScopeOptionsResult,
} from '../../gql/operations';

// Create/edit form for a quota (US-8.4.1). Scope (KEY/APP/CLIENT) + scopeId identify what the
// limit applies to; the four numeric limits (rpm, tpm, dailyTokenCap, maxConcurrency) are all
// optional. Save is blocked until a scopeId is present. A blank numeric field clears that limit.

const SCOPE_OPTIONS = [
  { value: QuotaScope.Key, label: 'Key — a single API key' },
  { value: QuotaScope.App, label: 'App — an application' },
  { value: QuotaScope.Client, label: 'Client — a tenant' },
] as const;

const SCOPE_ID_HINT: Record<QuotaScope, string> = {
  [QuotaScope.Key]: 'The API key id this limit applies to.',
  [QuotaScope.App]: 'The application id this limit applies to.',
  [QuotaScope.Client]: 'The client (tenant) id this limit applies to.',
};

export interface QuotaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing quota to edit, or null to create. */
  quota: Quota | null;
  loading?: boolean | undefined;
  onSubmit: (input: UpsertQuotaInput) => void;
}

/** Parse an optional positive integer field. Blank → null (clears the limit). */
function parseLimit(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return Math.trunc(value);
}

function limitToString(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

const QUOTA_SCOPE_VALUES = Object.values(QuotaScope) as string[];

/** Narrow an arbitrary select value to a real QuotaScope member. */
function isQuotaScope(value: string): value is QuotaScope {
  return QUOTA_SCOPE_VALUES.includes(value);
}

export function QuotaFormModal({
  isOpen,
  onClose,
  quota,
  loading = false,
  onSubmit,
}: QuotaFormModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isEdit = quota !== null;

  const [scope, setScope] = useState<QuotaScope>(QuotaScope.Key);
  const [scopeId, setScopeId] = useState('');
  const [rpm, setRpm] = useState('');
  const [tpm, setTpm] = useState('');
  const [dailyTokenCap, setDailyTokenCap] = useState('');
  const [maxConcurrency, setMaxConcurrency] = useState('');

  // Fetch the picker options on modal open (`enabled: isOpen` would be ideal but the hook
  // doesn't expose that — useQuery only runs once per mount, and the modal mounts when
  // the route does so the cost is incurred once. Cached + reusable across edits.)
  const scopeOptionsQuery = useQuery<QuotaScopeOptionsResult>(QUOTA_SCOPE_OPTIONS_QUERY);

  useEffect(() => {
    if (!isOpen) return;
    setScope(quota?.scope ?? QuotaScope.Key);
    setScopeId(quota?.scopeId ?? '');
    setRpm(limitToString(quota?.rpm));
    setTpm(limitToString(quota?.tpm));
    setDailyTokenCap(limitToString(quota?.dailyTokenCap));
    setMaxConcurrency(limitToString(quota?.maxConcurrency));
  }, [isOpen, quota]);

  // Reset the scopeId selection whenever the scope changes — the previously-picked id
  // is by definition the wrong type for the new scope (e.g. a key id makes no sense as
  // a client id). Skipped on initial seed (the effect above already set both together).
  const [scopeUserSelected, setScopeUserSelected] = useState(false);
  useEffect(() => {
    if (!scopeUserSelected) return;
    setScopeId('');
  }, [scope, scopeUserSelected]);

  const valid = scopeId.trim().length > 0;

  // Compute the picker options for the currently-selected scope. Labels fold the entity
  // name with its disambiguating context — `AutocompleteSelect` filters on the full label
  // string, so an operator can type "Acme" to find a client OR an app whose label
  // includes "Acme". Empty list → the placeholder switches to "No <entity> yet".
  const scopeIdOptions = useMemo(() => {
    const clients = scopeOptionsQuery.data?.clients ?? [];
    if (scope === QuotaScope.Client) {
      return clients.map((c) => ({
        value: c.id,
        label: `${c.name}  ·  ${c.id}`,
      }));
    }
    if (scope === QuotaScope.App) {
      const out: { value: string; label: string }[] = [];
      clients.forEach((c) => {
        c.applications.forEach((a) => {
          out.push({
            value: a.id,
            label: `${a.name}  ·  ${c.name}`,
          });
        });
      });
      return out;
    }
    // QuotaScope.Key
    const out: { value: string; label: string }[] = [];
    clients.forEach((c) => {
      c.applications.forEach((a) => {
        a.apiKeys.forEach((k) => {
          out.push({
            value: k.id,
            label: `${k.keyPrefix}…  ·  ${a.name}  ·  ${c.name}  ·  ${k.status.toLowerCase()}`,
          });
        });
      });
    });
    return out;
  }, [scopeOptionsQuery.data, scope]);

  const handleSubmit = () => {
    if (!valid) return;
    const input: UpsertQuotaInput = {
      scope,
      scopeId: scopeId.trim(),
      rpm: parseLimit(rpm),
      tpm: parseLimit(tpm),
      dailyTokenCap: parseLimit(dailyTokenCap),
      maxConcurrency: parseLimit(maxConcurrency),
    };
    if (isEdit && quota) input.id = quota.id;
    onSubmit(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit quota' : 'New quota'}
      subtitle="Rate and capacity limits for a key, app, or client. Leave a limit blank to leave it unbounded."
      icon={<Gauge className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Save className="h-4 w-4" aria-hidden="true" />}
          >
            Save quota
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <span
              className={`block text-xs font-medium ${
                isDark ? 'text-white/60' : 'text-slate-600'
              }`}
            >
              Scope <span aria-hidden="true">*</span>
            </span>
            <AutocompleteSelect
              label="Scope"
              value={scope}
              options={SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(next) => {
                if (isQuotaScope(next)) {
                  setScope(next);
                  setScopeUserSelected(true);
                }
              }}
            />
          </div>
          <div className="space-y-1.5">
            <span
              className={`block text-xs font-medium ${
                isDark ? 'text-white/60' : 'text-slate-600'
              }`}
            >
              {scope === QuotaScope.Key
                ? 'API key'
                : scope === QuotaScope.App
                  ? 'Application'
                  : 'Client'}{' '}
              <span aria-hidden="true">*</span>
            </span>
            <AutocompleteSelect
              label={
                scope === QuotaScope.Key
                  ? 'API key'
                  : scope === QuotaScope.App
                    ? 'Application'
                    : 'Client'
              }
              value={scopeId}
              options={scopeIdOptions}
              onChange={setScopeId}
              placeholder={
                scopeOptionsQuery.loading
                  ? 'Loading…'
                  : scopeIdOptions.length === 0
                    ? scope === QuotaScope.Key
                      ? 'No API keys yet'
                      : scope === QuotaScope.App
                        ? 'No applications yet'
                        : 'No clients yet'
                    : 'Search by name…'
              }
            />
            <span
              className={`block text-[11px] ${isDark ? 'text-white/40' : 'text-slate-500'}`}
            >
              {SCOPE_ID_HINT[scope]}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Requests per minute"
            type="number"
            min="0"
            inputMode="numeric"
            value={rpm}
            onChange={(e) => setRpm(e.target.value)}
            placeholder="Unbounded"
          />
          <TextField
            label="Tokens per minute"
            type="number"
            min="0"
            inputMode="numeric"
            value={tpm}
            onChange={(e) => setTpm(e.target.value)}
            placeholder="Unbounded"
          />
          <TextField
            label="Daily token cap"
            type="number"
            min="0"
            inputMode="numeric"
            value={dailyTokenCap}
            onChange={(e) => setDailyTokenCap(e.target.value)}
            placeholder="Unbounded"
          />
          <TextField
            label="Max concurrency"
            type="number"
            min="0"
            inputMode="numeric"
            value={maxConcurrency}
            onChange={(e) => setMaxConcurrency(e.target.value)}
            placeholder="Unbounded"
          />
        </div>
      </div>
    </Modal>
  );
}
